import React from "react";
import { getName } from "country-list";
import moment from "moment";
import {
  IStudentApplicationCardCenterContent,
  IStudentApplicationCardHeaderContent,
  IStudentApplicationCardFrontEndModel,
  IStudentLanguageCourseApplicationCardBackEndModel,
} from "../types";
import SchoolDefaultLogo from "../../../assets/img/school-default-logo.png";
import { beautifyRequiredDocuments } from "../../program/helpers/helperFunctions";
import { beautifyDurationUnit } from "../../../helpers";
import {
  calcFeeWithDiscount,
  sumPriceAndOtherFeesSupports,
} from "../../language-course/helpers/helperFunctions";
import { COURSE_TYPES, LANGUAGES } from "../../language-course/constants";
import { ColorName } from "../../../types";
import { CourseType } from "../../language-course/types";
import { ReactComponent as SubmissionDate } from "../../../assets/svg/hourglass.svg";
import { CardFieldType } from "../../program/components/constants";
import { ReactComponent as RequiredDocumentsInsideCircle } from "../../../assets/svg/required-documents-inside-circle.svg";
import { ExpandedRequiredDocuments } from "../../program/components/CardField/components/ExpandedRequiredDocuments";
import { ReactComponent as Location } from "../../../assets/svg/location-inside-circle.svg";
import { ReactComponent as ProgramDuration } from "../../../assets/svg/duration.svg";
import { Routes } from "../../../constants/routes";

function parser(
  application: IStudentLanguageCourseApplicationCardBackEndModel,
) {
  const capitalizedRequiredDocuments = beautifyRequiredDocuments(
    application.course.requiredDocs,
  );

  const { hasSupport: hasPriceSupport, calcFee: calcPrice } =
    calcFeeWithDiscount({
      fee: application.course.price,
      hasFeeDiscount: application.course.hasPriceDiscount,
      feeDiscountType: application.course.priceDiscountType,
      feeDiscountAmount: application.course.priceDiscountAmount,
      currency: application.course.currency,
    });

  const { hasSupport: hasOtherFeesSupport, calcFee: calcOtherFees } =
    calcFeeWithDiscount({
      fee: application.course.otherFeesTotal,
      hasFeeDiscount: application.course.hasOtherFeesDiscount,
      feeDiscountType: application.course.otherFeesDiscountType,
      feeDiscountAmount: application.course.otherFeesDiscountAmount,
      currency: application.course.currency,
    });

  const {
    priceOtherFeesSumWithCurrency,
    priceOtherFeesWithSupportsSumWithCurrency,
  } = sumPriceAndOtherFeesSupports(
    application.course.price,
    hasPriceSupport,
    calcPrice,
    application.course.otherFeesTotal,
    hasOtherFeesSupport,
    calcOtherFees,
    application.course.currency,
  );

  const courseDuration = `${
    application.course.durationAmount
  } ${beautifyDurationUnit(
    application.course.durationAmount,
    application.course.durationUnit,
    "Course",
  )}`;

  const priceDuration = `${
    application.course.priceDurationAmount == null
      ? ""
      : application.course.priceDurationAmount + " "
  }
        ${beautifyDurationUnit(
          application.course.priceDurationAmount,
          application.course.priceDurationUnit,
          "Course",
        )}`;

  const totalPriceWithUnit =
    priceOtherFeesWithSupportsSumWithCurrency + " / " + priceDuration;

  const schoolImage =
    application.course.school.image?.preview ?? SchoolDefaultLogo;

  const schoolCancellationNote = !application.schoolCancellationNote
    ? null
    : {
        ...application.schoolCancellationNote,
        date: moment(application.schoolCancellationNote.created_at).format(
          "ddd, MMM D, YYYY, h:mm A",
        ),
      };

  const studentCancellationNote = !application.studentCancellationNote
    ? null
    : {
        ...application.studentCancellationNote,
        date: moment(application.studentCancellationNote.created_at).format(
          "ddd, MMM D, YYYY, h:mm A",
        ),
      };

  const schoolCountry = application.branch.country
    ? getName(application.branch.country)!
    : "N/A";

  const headerContent: IStudentApplicationCardHeaderContent = {
    leftSection: {
      image: schoolImage,
      lRow0: {
        text0: application.course.school.name,
      },
      lRow1: {
        text0: LANGUAGES.find((l) => l.id === application.course.language)!
          .name,
      },
      lRow2: {
        text0: "Category - ",
        text1: application.course.category,
      },
    },
    rightSection: {
      rRow0: {
        text0: COURSE_TYPES.find(
          (type) => type.id === application.course.courseType,
        )!.name,
        text0BackgroundColor:
          application.course.courseType === CourseType.ONLINE
            ? ColorName.ONLINE
            : ColorName.ON_CAMPUS,
      },
      rRow1: {
        text0: totalPriceWithUnit,
        text1: priceOtherFeesSumWithCurrency,
      },
    },
  };

  const centerContent: IStudentApplicationCardCenterContent[] = [
    {
      id: CardFieldType.SubmissionDate,
      icon: SubmissionDate,
      title: "Submission Date",
      previewContent: [application.created_at],
    },
    {
      id: CardFieldType.RequiredDocuments,
      icon: RequiredDocumentsInsideCircle,
      title: "Required Documents",
      previewContent: capitalizedRequiredDocuments,
      expandedContent: () => (
        <ExpandedRequiredDocuments reqDocs={capitalizedRequiredDocuments} />
      ),
    },
    {
      id: CardFieldType.Country,
      icon: Location,
      title: "Country",
      previewContent: [schoolCountry],
    },
    {
      id: CardFieldType.CourseDuration,
      icon: ProgramDuration,
      title: "Course Duration",
      previewContent: [courseDuration],
    },
  ];

  const parsedApplication: IStudentApplicationCardFrontEndModel = {
    headerContent,
    centerContent,
    schoolCancellationNote,
    studentCancellationNote,
    id: application.id,
    schoolType: application.course.school.typeId,
    status: application.status,
    schoolId: application.course.school.id,
    submissionDate: application.created_at,
    redirectRoute: Routes.VIEW_COURSE,
    entityName: "course",
    entityId: application.course.id,
  };

  return parsedApplication;
}

export function parseToStudentCardsLanguageCourseFrontEndModel(response) {
  response.data = response.data?.map((program) => parser(program));

  return response;
}
